import React, { useMemo } from "react";
import Legend from "./legand";
import "./graphs.css";
import { COLORS, fmt } from "./utils";

export interface NutrientRow {
    FieldGuid: string;
    FieldName: string;
    NutrientName: string;
    VeryLow: number;
    Low: number;
    Medium: number;
    High: number;
    VeryHigh: number;
    AvgValue: number;
}

interface Field {
    name: string;
    nutrients: NutrientRow[];
}

function groupByField(data: NutrientRow[]): Field[] {
    const map = new Map<string, Field>();
    for (const row of data) {
        if (!map.has(row.FieldGuid)) {
            map.set(row.FieldGuid, { name: row.FieldName, nutrients: [] });
        }
        map.get(row.FieldGuid)!.nutrients.push(row);
    }

    const result: Field[] = [];
    for (const field of [...map.values()].sort((a, b) => a.name.localeCompare(b.name))) {
        if (field.nutrients.length <= 9) {
            result.push(field);
        } else {
            for (let i = 0; i < field.nutrients.length; i += 9) {
                const chunk = field.nutrients.slice(i, i + 9);
                const name = i === 0 ? field.name : `${field.name} (cont.)`;
                result.push({ name, nutrients: chunk });
            }
        }
    }
    return result;
}

function NutrientBar({ nutrient }: { nutrient: NutrientRow }) {
    const { NutrientName, VeryLow, Low, Medium, High, AvgValue } = nutrient;

    const displayTop = Math.max(High * 1.5, AvgValue * 1.1);

    const bands = [
        { from: 0, to: VeryLow, color: COLORS[0] },
        { from: VeryLow, to: Low, color: COLORS[1] },
        { from: Low, to: Medium, color: COLORS[2] },
        { from: Medium, to: High, color: COLORS[3] },
        { from: High, to: displayTop, color: COLORS[4] },
    ];

    const toPercent = (v: number) => 11 + (1 - Math.min(v / High, 1)) * 89;

    const avgTop = toPercent(AvgValue);

    const mainBands = bands.slice(0, 4);

    return (
        <div className="barSection">
            <div className="barContainer">
                <div style={{ height: "11%", background: COLORS[4] }} className="barPart">
                    <span>{fmt(High)}</span>
                </div>

                {mainBands.map((b, i) => {
                    const top = toPercent(b.to);
                    const bottom = toPercent(b.from);
                    const height = bottom - top;
                    if (height <= 0) return null;
                    return (
                        <div
                            key={i}
                            style={{
                                top: `${top}%`,
                                height: `${height}%`,
                                background: b.color,
                            }}
                            className="barPart"
                        >
                            <span>{fmt(b.from === 0 ? 0 : b.from)}</span>
                        </div>
                    );
                })}

                <div className="avgBlock" style={{ top: `${avgTop}%` }}>
                    <span>{fmt(AvgValue)}</span>
                </div>
            </div>

            <div className="nutrient">{NutrientName}</div>
        </div>
    );
}

function FieldSection({ field }: { field: Field }) {
    return (
        <div className="fieldSection">
            <div className="fieldName">{field.name}</div>
            <div className="barGroup">
                {field.nutrients.map((n) => (
                    <NutrientBar key={n.NutrientName} nutrient={n} />
                ))}
            </div>
        </div>
    );
}

interface NutrientChartProps {
    data: NutrientRow[];
    legendData: string[][];
}

export default function NutrientChart({ data, legendData }: NutrientChartProps) {
    const fields = useMemo(() => groupByField(data), [data]);

    return (
        <div className="analysisResultBarSection">
            {legendData === undefined ? <></> : <Legend legendData={legendData}></Legend>}
            {fields.map((field) => (
                <FieldSection key={field.name} field={field} />
            ))}
        </div>
    );
}
